import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
import Button from "pages/_components/Button";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import { ResponsiveHeader } from "pages/_components/header";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import Notification from "pages/_components/Notification";
import { actions as echeckActions } from "reducers/echecks";
import { useTransaction } from "hooks/useTransactions";
import { useDispatch } from "react-redux";
import { bool } from "prop-types";
import CesionTransactionBody from "./components/TransactionBody";
import CesionTransactionMultipleBody from "./components/TransactionMultipleBody";
import { isFinishedWithErrors } from "../utils"

const CesionEcheckTransactionPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const [showMoreData, setShowMoreData] = useState(false);
    const confirmRoute = `/echecks/action/cesion/confirm`;

    const formatTransactionEcheck = (data, idTransaction) => {
        const formData = {
            address: data.address,
            reference: data.reference,
            referencevalue: data.referencevalue,
            beneficiary: { document: data.beneficiary, reference: data.beneficiaryName },
            idTransaction,
        };

        dispatch(echeckActions.setEchecksToDoActions({ 
            action: data.action, 
            echeqList: data.echeqList, 
            specialAttribute: data.specialAttribute,
            idTransaction,
            cancelTransaction: false, 
        }));

        return { ...formData };
    };

    const { isLoading, transaction, handleSignTransaction, showSignButton, push, id, userId, status } = useTransaction(
        confirmRoute,
        echeckActions.echeqCesionData,
        echeckActions.cleanEcheckTicket,
        formatTransactionEcheck,
    );

    const { isBackoffice, isFinished, isPending, isCancelled, isFailed, isFlowComplete } = status;

    const getTitle = () => {
        const { data } = transaction;
        if( data?.echeqList?.length  > 1 ) {
            if (data?.action === "ANULATE-CESION") {
                return "echeck.anulate.cesion.multiple";
            }
             return "echeq.emit.cesion.multiple.echeq";
        }

        return  "echeq.emit.cesion.echeq";
    }

    const backLinkTo = isFlowComplete ? "/echecks" : "";
    const withErrorsState = isFinishedWithErrors(transaction?.data?.cessions) ? "FINISHED_WITH_ERRORS" : null;
    const title = getTitle();
    const subTitle = withErrorsState ? "ticket.status." + withErrorsState : null;

    const { componentRef, handlePrint, handlePrintMobile, mobileRef, handleShareMobile } = useReactPrint(
        title,
        id,
        "echecks/cesion",
    );

    const handlePrintWithFullData = () => {
        setShowMoreData(true);
        setTimeout(() => {
            handlePrint();
        }, 100);
    };

    const handlePrintMobileWithFullData = () => {
        setShowMoreData(true);
        setTimeout(() => {
            handlePrintMobile();
        }, 100);
    };

    const cancelTransaction = () => {
        const { data, idTransaction } = transaction;
        const cesionData = {
            address: data.address,
            reference: data.reference,
            referencevalue: data.referencevalue,
            beneficiary: {
                document: data.beneficiary,
                reference: data.beneficiaryName,
            },
            idTransaction,
            cancelTransaction: true,
            userId,
        };

        dispatch(
            echeckActions.echeqCesionData({
                ...cesionData,
                idTransaction,
                cancelTransaction: true,
                userId,
            }),
        );
        dispatch(echeckActions.setEchecksToDoActions({ 
            action: data.action, 
            echeqList: data.echeqList, 
            specialAttribute: data.specialAttribute,
            idTransaction,
            cancelTransaction: true,
        }));

        dispatch(push(confirmRoute));
    };

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const renderBackToHomeButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={() => dispatch(push("/desktop"))}
                label="forms.transaction.ticket.actions.backToHome"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );
    const renderDownloadButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleDownload}
                image="images/figma/icon/download.svg"
                label="global.download"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );
    const renderCancelButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={cancelTransaction}
                label="lotPayment.cancel"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderSignButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleSignTransaction}
                label="forms.signTransaction.link"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    return (
        <>
            {isFinished && <Notification scopeToShow="echecks/cesion" />}
            {!isBackoffice && <ResponsiveHeader ticket metaTitle={title}  backLinkTo={backLinkTo} />}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader title={title} status={withErrorsState} subTitle={subTitle}/>
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    {transaction.data?.echeqList?.length > 1 && (
                                        <CesionTransactionMultipleBody
                                            setShowMoreData={setShowMoreData}
                                            showMoreData={showMoreData}
                                            isDesktop={isDesktop}
                                        />
                                    )}
                                    {transaction.data?.echeqList?.length == 1 && (
                                        <CesionTransactionBody
                                            setShowMoreData={setShowMoreData}
                                            showMoreData={showMoreData}
                                            isDesktop={isDesktop}
                                        />
                                    )}
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            {(isFinished  || isCancelled ||
                                isFailed) && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderDownloadButton(true, 0)}
                                </>
                            )}{" "}
                            {isPending &&
                                (showSignButton ? (
                                    <>
                                        {renderCancelButton(false, 2)}
                                        {renderSignButton(true, 0)}
                                    </>
                                ) : (
                                    <>
                                        {renderBackToHomeButton(false, 2)}
                                        {renderCancelButton(true, 0)}
                                    </>
                                ))}
                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

CesionEcheckTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};
export default CesionEcheckTransactionPage;
